import type { JSX } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface ModuleNavProps {
  activeModule: string;
  /** Lights up (and SSO-links) the Safe Ministry tab for vetting-authorised users. */
  vettingAccess: boolean;
}

interface ModuleEntry {
  key: string;
  label: string;
  available: boolean;
  /** Opens another portal in a new tab instead of navigating in-app. */
  external?: boolean;
}

export function ModuleNav({ activeModule, vettingAccess }: ModuleNavProps): JSX.Element {
  const navigate = useNavigate();
  const { parishId } = useParams<{ parishId: string }>();

  const modules: ReadonlyArray<ModuleEntry> = [
    { key: 'identity', label: 'Identity', available: true },
    { key: 'personnel', label: 'Personnel', available: true },
    { key: 'safe-ministry', label: 'Safe Ministry', available: vettingAccess, external: true },
  ];

  const handleModuleClick = (m: ModuleEntry) => {
    if (m.external) {
      // SSO bridge → opens the Vetting Portal already signed-in. See the
      // worker's /api/sso/vetting handoff route.
      window.open('/api/sso/vetting', '_blank', 'noopener');
      return;
    }
    if (parishId) {
      navigate(`/parish/${parishId}/${m.key}`);
    } else {
      navigate(`/${m.key}`);
    }
  };

  return (
    <div className="tab-bar" style={{ overflowX: 'auto' }}>
      <div
        style={{
          display: 'flex',
          // Match <main>'s max-w-5xl (1024) + px-7 (28) so the module tabs line
          // up with the page content beneath them.
          maxWidth: 1024,
          margin: '0 auto',
          paddingLeft: 28,
          paddingRight: 28,
        }}
      >
        {modules.map((m) => {
          const isActive = m.key === activeModule;
          const title = m.available
            ? m.external
              ? `${m.label} — opens the Vetting Portal`
              : m.label
            : `${m.label} — coming soon`;
          return (
            <button
              key={m.key}
              onClick={() => m.available && handleModuleClick(m)}
              disabled={!m.available}
              className={`tab${isActive ? ' active' : ''}${!m.available ? ' tab-soon' : ''}`}
              title={title}
              type="button"
            >
              {m.label}
              {m.available && m.external && <span className="soon-label">↗</span>}
              {!m.available && <span className="soon-label">·soon</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
